import React, {FC, useContext, useEffect, useState, useMemo} from "react";
import {
	Box,
	BoxProps,
	Button,
	Card,
	CardContent,
	Chip,
	Typography,
	useMediaQuery,
	useTheme,
	CardMedia,
	Skeleton
} from "@mui/material";
import {useApi} from "@buildwitt/shapeshift-identity";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import placeholderImage from "@/icons/placeholder-image.png";
import {useTranslation} from "react-i18next";
import {palette} from "@/constants";

interface ICustomProps {
	certificationId: string;
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

interface CertificationResponseDto {
	id: string;
	certificationStatus: string;
	name: string;
	description?: string | null;
	certificationCode?: string | null;
	logoUrl?: string | null;
	grantingEntityName: string;
	grantingEntityId: string;
	createdBy: string;
	employeesAwarded: number;
	isReadOnly: boolean;
	isRecentlyAdded: boolean;
	createdAt: string;
	durationUnit?: string;
	durationPeriod?: number | null;
}

export const CertificationTab: FC<IProps> = ({certificationId, ...baseProps}) => {
	const theme = useTheme();
	const {t} = useTranslation();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [pageBaseState] = useContext(PageBaseContext);

	const cohortId = useMemo(() => {
		if (pageBaseState?.ownedCohort && typeof pageBaseState.ownedCohort === "object") {
			return pageBaseState.ownedCohort.id;
		}
		return null;
	}, [pageBaseState]);

	const [certification, setCertification] = useState<CertificationResponseDto | null>(null);
	const [{data: certificationResult, isLoading}] = useApi<any>(
		cohortId ? `/api/cohort/${cohortId}/groups/certifications/${certificationId}` : null
	);

	useEffect(() => {
		if (certificationResult) {
			setCertification(certificationResult);
		}
	}, [certificationResult]);

	const renderSkeleton = () => (
		<Card
			sx={{
				mt: theme.spacing(3),
				p: theme.spacing(2.5),
				display: "flex",
				flexDirection: isMobile ? "column" : "row",
				alignItems: "center",
				gap: 3
			}}
		>
			<Skeleton variant="rectangular" width={300} height={200} sx={{borderRadius: 2}} />
			<Box sx={{flex: 1, width: "100%"}}>
				<Skeleton variant="text" height={40} width={120} />
				<Skeleton variant="text" height={40} width="100%" />
				<Skeleton variant="text" height={40} width="100%" />
				<Skeleton variant="text" height={40} width="100%" />
			</Box>
		</Card>
	);

	if (isLoading || !certification) {
		return renderSkeleton();
	}

	return (
		<Box {...baseProps}>
			<Card
				sx={{
					mt: theme.spacing(3),
					p: theme.spacing(2.5),
					backgroundColor: theme.palette.background.paper,
					borderWidth: theme.borderWidth?.small || 1,
					borderColor: theme.palette.grey[100],
					borderStyle: "solid",
					borderRadius: theme.borderRadius?.small || 2,
					display: "flex",
					flexDirection: isMobile ? "column" : "row"
				}}
			>
				<CardMedia
					component="img"
					data-testid="certification-logo"
					aria-label="Certification Logo"
					src={certification.logoUrl || placeholderImage}
					alt={certification.name || "Certification Logo"}
					sx={{
						width: 300,
						height: 200,
						borderRadius: 2,
						backgroundColor: "#fff",
						objectFit: "contain"
					}}
				/>

				<CardContent
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						mx: isMobile ? 0 : 4,
						pt: isMobile ? 2 : 0,
						textAlign: "left",
						gap: 1.5
					}}
				>
					{certification.isRecentlyAdded && (
						<Chip
							label={t("certification_recently_added")}
							color="success"
							size="small"
							data-testid="recently-added-chip"
							aria-label="Recently Added"
							sx={{alignSelf: "flex-start"}}
						/>
					)}

					<Box
						sx={{
							display: "flex",
							flexDirection: isMobile ? "column" : "row",
							gap: 4,
							mb: 2
						}}
					>
						<Box sx={{width: "250px"}}>
							<Typography variant="subtitle2" color={palette.RxdDescription}>
								{t("common_name")}
							</Typography>
							<Typography variant="body1">{certification.name}</Typography>
						</Box>

						{certification.description && (
							<Box sx={{width: "100%"}}>
								<Typography variant="subtitle2" color={palette.RxdDescription}>
									{t("common_description")}
								</Typography>
								<Typography variant="body1">{certification.description}</Typography>
							</Box>
						)}
					</Box>

					<Box>
						<Typography variant="subtitle2" color={palette.RxdDescription}>
							{t("certification_employees_awarded")}
						</Typography>
						<Typography variant="body1">{certification.employeesAwarded}</Typography>
					</Box>
				</CardContent>

				<Button
					variant="contained"
					data-testid="certification_manage-button"
					aria-label="Certification Manage"
					onClick={() => { window.open(process.env.REACT_APP_PROPELAUTH_ACCOUNT_SETTINGS_URI + "/certifications/detail/" + certificationId, "_blank"); }}
					color="warning"
					startIcon={<OpenInNewIcon />}
					sx={{alignSelf: isMobile ? "center" : "flex-start"}}
				>
					{t("certification_manage")}
				</Button>
			</Card>
		</Box>
	);
};
